import { makeAutoObservable, runInAction } from 'mobx'
import { PageLayoutStore } from 'shared/layout'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { FeatureRestrictedModalStore } from 'entities/UsersPermissions'
import { KeywordsApi } from 'entities/Keywords/api/keywords'
import { SettingsApi } from 'entities/Settings'
import { ChatbotApi } from 'entities/Chatbot'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import { CampaignApi } from 'entities/Campaign'
import { WorkflowsApi } from 'entities/Workflow'
import { PowerDialerApi } from 'entities/PowerDialer/api/powerDialer'
import type { IDiscoverPagesKey, IDiscoverBoxData, IDiscoverBoxKey } from 'pages/discover/lib/types'
import { getDiscoverBoxesDataMap } from 'pages/discover/lib/discoverData'

export class DiscoverStore {
  private _loading = true
  private _listLoading = true
  private _page: IDiscoverPagesKey | null = null
  private _itemsMap: Map<IDiscoverBoxKey, IDiscoverBoxData> = new Map()
  private _pageLayoutStore = new PageLayoutStore()

  private get _discoverBoxDataMap() {
    return getDiscoverBoxesDataMap(this._entities.featureFlagsStore)
  }
  private readonly _discoverPagesBoxes: Record<IDiscoverPagesKey, IDiscoverBoxKey[]> = {
    capture: [
      'keywords',
      'websiteChatWidget',
      'missedCallAutomation',
      'clickToText',
      'qrCode',
      'swipeUpToText',
    ],
    connect: ['ai', 'broadcasts', 'campaigns', 'workflows', 'smartCallFlows', 'ivr', 'powerDialer'],
    convert: [
      'ai',
      'broadcasts',
      'campaigns',
      'workflows',
      'conversionTracking',
      'smartCallFlows',
      'ivr',
      'powerDialer',
    ],
    care: ['ai', 'smartCallFlows', 'ivr', 'surveys', 'reviews', 'referrals'],
  }

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    void this.init()
  }

  private get _boxHandlers(): Partial<Record<IDiscoverBoxKey, () => Promise<boolean>>> {
    return {
      keywords: this.isKeywordActive,
      websiteChatWidget: this.isWebsiteChatWidgetActive,
      ai: this.isAiActive,
      broadcasts: this.isBroadcastsActive,
      campaigns: this.isCampaignsActive,
      workflows: this.isWorkflowsActive,
      powerDialer: this.isPowerDialerActive,
    }
  }

  init = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      await Promise.all([
        this._entities.usersStore.initPromise,
        this._entities.userPermissionsStore.initPromise,
        this._entities.organizationStore.initPromise,
      ])
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get loading() {
    return this._loading
  }

  get listLoading() {
    return this._listLoading
  }

  setPage = (page: IDiscoverPagesKey) => {
    this._page = page
    this.setItemsMap()
    void this.checkActiveBoxes()
  }

  setItemsMap() {
    if (!this._page) return

    this._itemsMap = new Map()

    this._discoverPagesBoxes[this._page].forEach((item: IDiscoverBoxKey) => {
      const itemData = this._discoverBoxDataMap[item]

      if (itemData !== undefined) this._itemsMap.set(item, itemData)
    })
  }

  get boxesList() {
    return Array.from(this._itemsMap.values())
  }

  logClickEvent = (title: string) => {
    this._entities.eventLogStore.logEvent(
      'Discover Section Redirected',
      {
        userId: this._entities.usersStore.user?.id || 'anonymous_user',
        event_id: 'discover_section_redirected',
        action: title,
        agencyId: this._entities.organizationStore.agency_id,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  logClickToTextEvent = (action: string) => {
    this._entities.eventLogStore.logEvent(
      'Click to Text Used',
      {
        event_id: 'click_to_text_used',
        action: action,
        agencyId: this._entities.organizationStore.agency_id,
      },
      { groupId: this._entities.organizationStore.id }
    )
  }

  handleCardClick = (box: IDiscoverBoxData) => {
    if (
      box.requiredPermission &&
      !this._entities.userPermissionsStore.getItem(box.requiredPermission)
    ) {
      new FeatureRestrictedModalStore(box.requiredPermission)
      return
    }

    if (!box.redirectTo) {
      return
    }

    if (box.id === 'clickToText') {
      this.logClickToTextEvent('Click to Text tile clicked')
    } else if (box.id === 'qrCode') {
      this.logClickToTextEvent('QR code tile clicked')
    } else if (box.id === 'swipeUpToText') {
      this.logClickToTextEvent('Swipe up to text tile clicked')
    }

    this.logClickEvent(box.title)

    uiStore.changeRouteViaVue({
      path: `/${box.redirectTo}`,
    })
  }

  checkActiveBoxes = async () => {
    if (!this._page) return

    runInAction(() => {
      this._listLoading = true
    })

    const pageBoxes = this._discoverPagesBoxes[this._page]
    const result = await Promise.all(
      pageBoxes.map(async (box) => {
        const value = await this.getItemPromise(box)
        return { box, value }
      })
    )

    result.forEach((item) => {
      const itemMap = this._itemsMap.get(item.box)

      if (item.value && itemMap) {
        itemMap.isActive = item.value
        this._itemsMap.set(item.box, itemMap)
      }
    })

    runInAction(() => {
      this._listLoading = false
    })
  }

  isKeywordActive = async () => {
    try {
      const {
        data: { data },
      } = await KeywordsApi.getKeywords({
        limit: 1,
      })

      return !!data.length
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  isWebsiteChatWidgetActive = async () => {
    try {
      const { data } = await SettingsApi.getWebsiteChatWidgetSettings()

      return data.status
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  isAiActive = async () => {
    try {
      const {
        data: { data },
      } = await ChatbotApi.getChatbotList({
        page: 1,
        limit: 1,
      })

      return !!data.length
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  isBroadcastsActive = async () => {
    try {
      const {
        data: { data },
      } = await BroadcastApi.getBroadcasts({
        type: 'all',
        length: 10,
      })

      return !!data.length
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  isCampaignsActive = async () => {
    try {
      const {
        data: { data },
      } = await CampaignApi.getList({
        search: '',
        limit: 1,
        page: 1,
      })

      return !!data.length
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  isWorkflowsActive = async () => {
    try {
      const {
        data: { payload },
      } = await WorkflowsApi.getWorkflows({
        'page[size]': 1,
      })

      return !!payload.length
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  isPowerDialerActive = async () => {
    try {
      const {
        data: { data },
      } = await PowerDialerApi.getPowerDialerList({
        limit: 10,
        search: '',
      })

      return !!data.length
    } catch (e) {
      logger.error(e)
    }

    return false
  }

  getItemPromise(box: IDiscoverBoxKey) {
    return this._boxHandlers[box]?.() ?? Promise.resolve(false)
  }
}
